import { defineStore } from 'pinia'
import { saveCart, getCartList } from '@/api/cart'
import { useUserStore } from './user'
import type { CartVo } from '@/types/cart'

interface CartItem {
  id?: number
  code?: string
  categoryName?: string
  name: string
  price: number
  mainImage?: string
  quantity: number
  description?: string
  dataPdf?: string
  checked?: boolean
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [] as CartItem[]
  }),
  getters: {
    totalCount(state) {
      return state.items.reduce((sum, item) => sum + item.quantity, 0)
    },
    totalPrice(state) {
      return state.items.reduce((sum, item) => sum + item.price * item.quantity, 0)
    }
  },
  actions: {
    async addItem(item: Omit<CartItem, 'quantity'>) {
      const userStore = useUserStore()
      const userId = userStore.userInfo?.id
      if (!userId) return
      const existingItem = this.items.find(i => i.id === item.id)
      let quantity = 1
      if (existingItem) {
        existingItem.quantity++
        quantity = existingItem.quantity
      } else {
        this.items.push({ ...item, quantity: 1 })
      }
      await saveCart({
        userId,
        productId: item.id!,
        quantity,
        checked: false,
        deleted: 0
      })
    },
    async removeItem(itemId: number) {
      const userStore = useUserStore()
      const userId = userStore.userInfo?.id
      if (!userId) return
      const index = this.items.findIndex(item => item.id === itemId)
      if (index > -1) {
        this.items.splice(index, 1)
        await saveCart({
          userId,
          productId: itemId,
          quantity: 0,
          checked: false,
          deleted: 1
        })
      }
    },
    async updateQuantity(itemId: number, quantity: number) {
      if (quantity < 1) return
      const userStore = useUserStore()
      const userId = userStore.userInfo?.id
      if (!userId) return
      const item = this.items.find(item => item.id === itemId)
      if (item) {
        item.quantity = quantity
        await saveCart({
          userId,
          productId: itemId,
          quantity,
          checked: item.checked ?? false,
          deleted: 0
        })
      }
    },
    async clearCart() {
      const userStore = useUserStore()
      const userId = userStore.userInfo?.id
      if (!userId) return
      // 清空本地
      this.items = []
      // 可选：遍历所有商品，逐个同步到后端（quantity: 0）
      for (const item of this.items) {
        await saveCart({ userId, productId: item.id!, quantity: 0, checked: false, deleted: 1 })
      }
    },
    async loadCartFromServer() {
      const userStore = useUserStore()
      const userId = userStore.userInfo?.id
      if (!userId) return
      try {
        const res = await getCartList(userId)
        this.items = (res.data || []).map((item: CartVo) => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          checked: item.checked,
          mainImage: item.mainImage,
          categoryName: item.categoryName,
          description: item.description,
          dataPdf: item.dataPdf
        }))
      } catch (error) {
        console.error('购物车数据加载失败:', error)
      }
    }
  },
  persist: true
})
